// Out-of-bundle template loader seam (Requirement 9.2, 9.3, 9.6).
//
// The workspace depends ONLY on `TemplateSource.loadTemplate(id)` returning a
// `MachineCodingTemplate`. Where the body comes from (a dynamic `import()` of a
// per-template module today, prebuilt CDN JSON later) is invisible to the
// workspace, so the rendering contract never changes.

import type { MachineCodingTemplate, TemplateSummary } from './types';
import { machineCodingCatalogIndex } from './catalogIndex';

/** Thrown when an id does not map to a registered/known template. */
export class TemplateNotFoundError extends Error {
 constructor(public readonly templateId: string) {
  super(`Machine coding template not found: ${templateId}`);
  this.name = 'TemplateNotFoundError';
  // Preserve prototype chain across down-level compilation targets.
  Object.setPrototypeOf(this, TemplateNotFoundError.prototype);
 }
}

export interface TemplateSource {
 /** Lightweight metadata for the catalog; never includes a Source_Tree. */
 getCatalogIndex(): TemplateSummary[];
 /** Resolve a full template body on demand. Rejects with TemplateNotFoundError. */
 loadTemplate(id: string): Promise<MachineCodingTemplate>;
}

/**
 * Static registry of per-template dynamic importers. One entry per template so
 * the bundler can code-split each body into its own chunk — adding a template
 * does not grow the initial bundle.
 */
type TemplateImporter = () => Promise<{ default: MachineCodingTemplate }>;

const templateImporters: { [id: string]: TemplateImporter } = {
 'splitwise-expense-sharing': () => import('./splitwise-expense-sharing'),
 'user-module-clean-architecture': () => import('./user-module-clean-architecture'),
 'uber-ride-booking': () => import('./uber-ride-booking'),
 'netflix-video-streaming': () => import('./netflix-video-streaming'),
 'custom-blank': () => import('./custom-blank'),
};

/** Current implementation: dynamic import per template id, from the code bundle. */
export const bundleTemplateSource: TemplateSource = {
 getCatalogIndex(): TemplateSummary[] {
  return machineCodingCatalogIndex;
 },

 async loadTemplate(id: string): Promise<MachineCodingTemplate> {
  const importer = templateImporters[id];
  if (!importer) {
   throw new TemplateNotFoundError(id);
  }
  const mod = await importer();
  return mod.default;
 },
};

/**
 * Capability seam only — NOT an active code path (Req 9.6). A future
 * `CdnTemplateSource` would implement the same `TemplateSource` contract by
 * fetching prebuilt JSON bodies from a content-delivery source, leaving the
 * workspace rendering contract unchanged.
 */
export interface CdnTemplateSource extends TemplateSource {
 /** Base URL of the content-delivery source serving prebuilt template JSON. */
 readonly baseUrl: string;
}

/** Default source the app uses; swappable to a CdnTemplateSource later. */
export const templateSource: TemplateSource = bundleTemplateSource;
